// Words and money for the billing screens (DESIGN.md §9: `KES 12,500`, sentence case).
import { formatMoney, moneyFromCents } from '@wodi/domain';
import type { AmountView, BillView, ChargeView } from '@wodi/shared';
import type { TagState } from '@wodi/ui';

/** `KES 12,500` from an amount on the wire. */
export function money(amount: AmountView): string {
  return formatMoney(moneyFromCents(BigInt(amount.cents)));
}

/** `12,500` for a numeric table column whose header names the currency. */
export function moneyColumn(amount: AmountView): string {
  return formatMoney(moneyFromCents(BigInt(amount.cents)), { showCurrency: false });
}

export function cents(amount: AmountView): bigint {
  return BigInt(amount.cents);
}

export type PaymentMethodKey = BillView['payments'][number]['method'];

export const METHOD_LABELS: Record<PaymentMethodKey, string> = {
  cash: 'Cash',
  mpesa: 'M-Pesa',
  card: 'Card',
  bank_transfer: 'Bank transfer',
};

/** The line's state in words, and the tag shape that goes with it (no colours, DESIGN.md). */
export const LINE_STATE: Record<ChargeView['state'], { text: string; tag: TagState }> = {
  owed: { text: 'Owes', tag: 'pending' },
  part_paid: { text: 'Part paid', tag: 'pending' },
  paid: { text: 'Paid', tag: 'done' },
  nothing_to_pay: { text: 'Nothing to pay', tag: 'done' },
  not_priced: { text: 'Not priced', tag: 'attention' },
  cancelled: { text: 'Cancelled', tag: 'neutral' },
};

/** "Patient owes KES 1,700 of KES 2,500." as the bill's first sentence (plan §6.1). */
export function billSentence(bill: BillView): string {
  const t = bill.totals;
  const parts: string[] = [];
  if (cents(t.owed) === 0n) parts.push(cents(t.due) === 0n ? 'Nothing to pay.' : 'Paid in full.');
  else parts.push(`Patient owes ${money(t.owed)} of ${money(t.due)}.`);
  if (bill.payer.kind !== 'cash') parts.push(`${bill.payer.name} pays ${money(t.payerAmount)}.`);
  if (cents(t.adjusted) > 0n) parts.push(`${money(t.adjusted)} waived or discounted.`);
  if (cents(t.patientCredit) > 0n) parts.push(`${money(t.patientCredit)} in credit.`);
  if (t.unpricedCount > 0)
    parts.push(
      t.unpricedCount === 1
        ? 'One charge has no price yet.'
        : `${String(t.unpricedCount)} charges have no price yet.`,
    );
  return parts.join(' ');
}
